import { Column, Row } from '@/components/container/Container';
import Spinner from '@/components/utils/Spinner';
import {
	TextBodyExtraExtraSmall,
	TextBodySmall,
	TextLabelLarge2,
	TextTitleMedium2,
} from '@/components/text/Texts';
import { cn } from '@/components/ui/cn';
import programStyle from '@/modules/programs/views/program.module.css';
import { RadioButton } from 'primereact/radiobutton';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import {
	MdCheckroom,
	MdCrop,
	MdDeleteOutline,
	MdDescription,
	MdLocalHospital,
	MdMoreHoriz,
	MdPhoneAndroid,
	MdRestaurant,
	MdArrowDropDown,
	MdUpload,
	MdWineBar,
} from 'react-icons/md';
import type { VehicleTypeDto } from '@/modules/vehicle/engine/ActivityTypes';
import {
	getDeliverySendVehicleTypeInfo,
	vehicleTypes,
	XpressVehicleType,
} from '@/modules/vehicle/engine/VehicleType';
import {
	firstActiveVehicleType,
	readVehicleTypeIsActive,
	SectionHeading,
} from './NewSendBookingHelpers';
import {
	DeliveryItemType,
	DeliverySize,
	type DeliveryPayerOption,
	type NewSendBookingFormValues,
	isDeliveryPayerSelectable,
} from './formTypes';

const PARCEL_SIZE_OPTIONS: {
	size: DeliverySize;
	imageSrc: string;
	label: string;
	detail: string;
}[] = [
	{
		size: DeliverySize.Small,
		imageSrc: '/images/concierge/delivery/small_package.png',
		label: 'Small',
		detail: '25cm × 25cm',
	},
	{
		size: DeliverySize.Medium,
		imageSrc: '/images/concierge/delivery/medium_package.png',
		label: 'Medium',
		detail: '40cm × 40cm',
	},
];

type PayerCardIcon = 'logo' | 'sender' | 'recipient';

const PAYER_CARD_DEFS: {
	id: DeliveryPayerOption;
	title: string;
	icon: PayerCardIcon;
	/** When set, shown instead of sender/recipient subtitle. */
	fixedSub?: string;
}[] = [
	{
		id: 'x4b',
		title: 'X4B Program',
		icon: 'logo',
		fixedSub: 'Billed to company',
	},
	{
		id: 'cash_sender',
		title: 'Cash by Sender',
		icon: 'sender',
	},
	{
		id: 'cash_recipient',
		title: 'Cash by Recipient',
		icon: 'recipient',
	},
];

/** UI rows for `DeliveryItemType` (excludes `None`). */
const DELIVERY_ITEM_OPTIONS: {
	type: DeliveryItemType;
	label: string;
	Icon: typeof MdDescription;
}[] = [
	{
		type: DeliveryItemType.Document,
		label: 'Document',
		Icon: MdDescription,
	},
	{ type: DeliveryItemType.Food, label: 'Food & Beverages', Icon: MdRestaurant },
	{ type: DeliveryItemType.Clothing, label: 'Clothing', Icon: MdCheckroom },
	{
		type: DeliveryItemType.Electronic,
		label: 'Electronic',
		Icon: MdPhoneAndroid,
	},
	{ type: DeliveryItemType.Fragile, label: 'Fragile', Icon: MdWineBar },
	{ type: DeliveryItemType.Medical, label: 'Medical Supplies', Icon: MdLocalHospital },
	{ type: DeliveryItemType.Other, label: 'Other', Icon: MdMoreHoriz },
];

const WEIGHT_OPTIONS: { maxKg: number; label: string }[] = [
	{ maxKg: 1, label: 'Below 1 kg' },
	{ maxKg: 5, label: '1–5 kg' },
	{ maxKg: 10, label: '5–10 kg' },
	{ maxKg: 20, label: '10–20 kg' },
];

function formatFileSizeForDisplay(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) {
		const rounded = kb >= 100 ? Math.round(kb) : Math.round(kb * 10) / 10;
		return `${rounded} KB`;
	}
	const mb = kb / 1024;
	return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

type Step2Errors = Partial<
	Record<
		| 'deliveryItemType'
		| 'parcelSize'
		| 'weightMaxKg'
		| 'parcelPhoto'
		| 'payer',
		string
	>
>;

type Props = {
	control: Control<NewSendBookingFormValues>;
	senderSubtitle: string;
	recipientSubtitle: string;
	parcelPhoto: File | null;
	/** Object URL from parent (stable across step changes). */
	parcelPhotoPreviewUrl: string | null;
	onParcelPhotoChange: (file: File | null) => void;
	errors: Step2Errors;
	routeGeoReady: boolean;
	vehicleTypesForPricing: VehicleTypeDto[] | undefined;
	routePricingLoading: boolean;
	routePricingIsError: boolean;
	routePricingErrorMessage: string;
	deliveryVehicleTypeId: XpressVehicleType;
	onDeliveryVehicleTypeIdChange: (id: XpressVehicleType) => void;
};

const NewSendBookingStep2 = ({
	control,
	senderSubtitle,
	recipientSubtitle,
	parcelPhoto,
	parcelPhotoPreviewUrl,
	onParcelPhotoChange,
	errors,
	routeGeoReady,
	vehicleTypesForPricing,
	routePricingLoading,
	routePricingIsError,
	routePricingErrorMessage,
	deliveryVehicleTypeId,
	onDeliveryVehicleTypeIdChange,
}: Props) => {
	const fileInputId = useId();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);

	const openParcelFilePicker = useCallback(() => {
		const el = fileInputRef.current;
		if (el) el.value = '';
		el?.click();
	}, []);

	const clearParcelPhoto = useCallback(() => {
		onParcelPhotoChange(null);
		const el = fileInputRef.current;
		if (el) el.value = '';
	}, [onParcelPhotoChange]);

	const onFileInput = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const f = e.target.files?.[0];
			onParcelPhotoChange(f ?? null);
		},
		[onParcelPhotoChange],
	);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			const f = e.dataTransfer.files?.[0];
			if (f && /^image\/(jpeg|png)$/i.test(f.type)) {
				onParcelPhotoChange(f);
			}
		},
		[onParcelPhotoChange],
	);

	const hasActiveVehicleType = useMemo(
		() => !!vehicleTypesForPricing?.some((t) => readVehicleTypeIsActive(t)),
		[vehicleTypesForPricing],
	);

	const selectedVehicleUi = useMemo(() => {
		if (vehicleTypesForPricing?.length) {
			const chosen = vehicleTypesForPricing.find(
				(x) => x.VehicleTypeId === deliveryVehicleTypeId,
			);
			const vt =
				chosen && readVehicleTypeIsActive(chosen)
					? chosen
					: (firstActiveVehicleType(vehicleTypesForPricing) ??
						vehicleTypesForPricing[0]);
			const display = getDeliverySendVehicleTypeInfo(
				vt.VehicleTypeId,
				vt.Amount,
			);
			return {
				display,
				showFare: readVehicleTypeIsActive(vt),
			};
		}
		const local = vehicleTypes.find(
			(v) => v.xpressVehicleType === deliveryVehicleTypeId,
		);
		const id = local
			? deliveryVehicleTypeId
			: XpressVehicleType.Sedan;
		return {
			display: getDeliverySendVehicleTypeInfo(id, 0),
			showFare: true,
		};
	}, [vehicleTypesForPricing, deliveryVehicleTypeId]);

	return (
		<Column className="new-send-booking-step2 gap-5 py-1">
			<TextTitleMedium2
				text="Parcel details & payer selection"
				className="text-primary"
			/>
			<Column className="gap-2.5">
				<SectionHeading title="Item Type" required />
				<Controller
					name="deliveryItemType"
					control={control}
					render={({ field }) => (
						<Row className="flex-wrap gap-1.5">
							{DELIVERY_ITEM_OPTIONS.map(({ type, label, Icon }) => {
								const isOn = field.value === type;
								return (
									<button
										key={type}
										type="button"
										onClick={() => field.onChange(type)}
										className={cn(
											'inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 border transition',
											'font-poppins text-label-medium',
											isOn
												? 'border-primary60 bg-primary99 text-primary shadow-sm'
												: 'border-[#CCDFE5] bg-primary99/60 text-onSurface hover:bg-primary99',
										)}
									>
										<Icon
											className="shrink-0 text-primary60"
											size={15}
										/>
										<span>{label}</span>
									</button>
								);
							})}
						</Row>
					)}
				/>
				{errors.deliveryItemType ? (
					<TextBodyExtraExtraSmall
						className="text-error60"
						text={errors.deliveryItemType}
					/>
				) : null}
			</Column>

			<Column className="gap-2.5">
				<SectionHeading title="Size" required />
				<Controller
					name="parcelSize"
					control={control}
					render={({ field }) => (
						<Row className="gap-1.5 w-full flex-wrap">
							{PARCEL_SIZE_OPTIONS.map(({ size, imageSrc, label, detail }) => {
								const checked = field.value === size;
								const select = () => field.onChange(size);
								return (
									<div
										key={size}
										className={cn(
											'relative flex-1 min-w-[6rem] rounded-[12px] border p-2.5 text-left transition',
											checked
												? 'border-primary60 bg-primary99 shadow-sm'
												: 'border-[#CCDFE5] bg-white hover:bg-primary99/50',
										)}
									>
										<button
											type="button"
											onClick={select}
											className="w-full border-0 bg-transparent p-0 pr-8 text-left cursor-pointer"
										>
											<Column className="w-full gap-1 items-start">
												<img
													src={imageSrc}
													alt=""
													className="block h-8 w-auto max-w-[4.4rem] object-contain object-left"
												/>
												<span className="block w-full font-poppins text-label-large2 text-primary font-semibold">
													{label}
												</span>
												<span className="block w-full font-poppins text-body-small text-onSurfaceVariant line-clamp-2">
													{detail}
												</span>
											</Column>
										</button>
										<div className="absolute right-2 top-2">
											<RadioButton
												inputId={`parcel-size-${size}`}
												value={size}
												checked={checked}
												onChange={select}
												pt={{
													box: {
														className:
															programStyle[
																'program-radio-input'
															],
													},
													icon: {
														className:
															programStyle[
																'program-radio-icon'
															],
													},
												}}
											/>
										</div>
									</div>
								);
							})}
						</Row>
					)}
				/>
				{errors.parcelSize ? (
					<TextBodyExtraExtraSmall
						className="text-error60"
						text={errors.parcelSize}
					/>
				) : null}
			</Column>

			<Column className="gap-2.5">
				<SectionHeading title="Weight" required />
				<Controller
					name="weightMaxKg"
					control={control}
					render={({ field }) => (
						<Row className="flex-wrap gap-1.5">
							{WEIGHT_OPTIONS.map(({ maxKg, label }) => {
								const isOn = field.value === maxKg;
								return (
									<button
										key={maxKg}
										type="button"
										onClick={() => field.onChange(maxKg)}
										className={cn(
											'rounded-full px-2.5 py-1.5 border font-poppins text-label-medium transition',
											isOn
												? 'border-primary60 bg-primary99 text-primary shadow-sm'
												: 'border-[#CCDFE5] bg-primary99/60 text-onSurface hover:bg-primary99',
										)}
									>
										{label}
									</button>
								);
							})}
						</Row>
					)}
				/>
				{errors.weightMaxKg ? (
					<TextBodyExtraExtraSmall
						className="text-error60"
						text={errors.weightMaxKg}
					/>
				) : null}
			</Column>

			<Column className="gap-2.5">
				<SectionHeading title="Parcel photo" required />
				<input
					ref={fileInputRef}
					id={fileInputId}
					type="file"
					accept="image/jpeg,image/png"
					className="sr-only"
					onChange={onFileInput}
				/>
				{parcelPhoto && parcelPhotoPreviewUrl ? (
					<div
						className={cn(
							'w-full rounded-[12px] border border-dashed border-primary60/70 bg-primary99/40 px-4 py-3 transition',
							errors.parcelPhoto && 'border-error60',
						)}
						onDragOver={(e) => e.preventDefault()}
						onDrop={onDrop}
					>
						<Column className="w-full gap-2">
							<Row className="w-full min-w-0 items-center gap-3">
								<div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-[#CCDFE5] bg-white">
									<img
										src={parcelPhotoPreviewUrl}
										alt=""
										className="h-full w-full object-cover"
									/>
									<span
										className="absolute bottom-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded bg-black/45 text-white"
										aria-hidden
									>
										<MdCrop size={12} />
									</span>
								</div>
								<Column className="min-w-0 flex-1 gap-0.5">
									<span className="truncate font-poppins text-label-large2 font-medium text-onSurface">
										{parcelPhoto.name}
									</span>
									<span className="font-poppins text-body-small text-onSurfaceVariant">
										{formatFileSizeForDisplay(parcelPhoto.size)}
									</span>
								</Column>
							</Row>
							<Row className="w-full flex-wrap items-center justify-end gap-1.5 border-t border-[#CCDFE5]/80 pt-2">
								<button
									type="button"
									onClick={clearParcelPhoto}
									className="inline-flex items-center gap-1.5 rounded-full border border-error60 bg-white px-4 py-1.5 font-poppins text-label-large2 text-error60 transition hover:bg-error99/40"
								>
									<MdDeleteOutline size={18} className="shrink-0" />
									Remove
								</button>
								<button
									type="button"
									onClick={openParcelFilePicker}
									className="inline-flex items-center gap-1.5 rounded-full bg-primary60 px-4 py-1.5 font-poppins text-label-large2 text-white transition hover:bg-primary50"
								>
									<MdUpload size={18} className="shrink-0" />
									Replace Uploaded
								</button>
							</Row>
						</Column>
					</div>
				) : (
					<button
						type="button"
						onClick={openParcelFilePicker}
						onDragOver={(e) => e.preventDefault()}
						onDrop={onDrop}
						className={cn(
							'w-full cursor-pointer rounded-[12px] border border-dashed border-primary60/70 bg-primary99/40 px-3 py-6 text-center transition hover:bg-primary99/70',
							errors.parcelPhoto && 'border-error60',
						)}
					>
						<Column className="gap-1.5 items-center">
							<MdUpload className="text-primary60" size={26} />
							<span className="font-poppins text-body-large text-onSurface">
								Choose a file or drag &amp; drop it here
							</span>
							<span className="font-poppins text-body-small text-onSurfaceVariant">
								JPEG, PNG formats, up to 10MB
							</span>
							<span className="mt-1.5 inline-flex rounded-full bg-primary60 px-4 py-1.5 font-poppins text-label-large2 text-white pointer-events-none">
								Browse file
							</span>
						</Column>
					</button>
				)}
				{errors.parcelPhoto ? (
					<TextBodyExtraExtraSmall
						className="text-error60"
						text={errors.parcelPhoto}
					/>
				) : null}

				<Controller
					name="driverNotes"
					control={control}
					render={({ field }) => (
						<textarea
							{...field}
							rows={3}
							placeholder="Add notes to driver (optional)"
							className={cn(
								'w-full min-h-[4rem] resize-y rounded-[4px] border border-primary60/80 px-2.5 py-1.5',
								'font-poppins text-body-large text-onSurface placeholder:text-neutralVariant20',
								'focus:outline-none focus:shadow-[inset_0_0_0_2px_theme(colors.primary60)]',
							)}
						/>
					)}
				/>
			</Column>

			<Column className="gap-2">
				<SectionHeading title="Vehicle type" required />
				{!routeGeoReady ? (
					<Row className="min-h-[3rem] items-center rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2">
						<TextBodySmall
							className="text-onSurfaceVariant"
							text="Complete pickup and drop-off on step 1 to load vehicle options."
						/>
					</Row>
				) : null}
				{routeGeoReady && routePricingLoading ? (
					<Row className="min-h-[3rem] items-center gap-1.5 rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2">
						<Spinner size="inline" />
						<TextBodySmall
							className="text-primary60"
							text="Loading available vehicles…"
						/>
					</Row>
				) : null}
				{routeGeoReady && !routePricingLoading && routePricingIsError ? (
					<Row className="min-h-[3rem] items-center rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2">
						<TextBodySmall
							className="text-error60"
							text={routePricingErrorMessage}
						/>
					</Row>
				) : null}
				{routeGeoReady &&
				!routePricingLoading &&
				!routePricingIsError &&
				!!vehicleTypesForPricing?.length &&
				hasActiveVehicleType ? (
					<div className="relative new-send-booking-vehicle-picker">
						<Row
							className={cn(
								'cursor-pointer items-center gap-2 rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2',
								vehicleDropdownOpen ? 'rounded-b-none border-b-0' : '',
							)}
							onClick={() =>
								setVehicleDropdownOpen((open) => !open)
							}
						>
							<div className="flex h-10 w-10 shrink-0 items-center justify-center self-center">
								<img
									className="max-h-8 max-w-8 object-contain"
									src={
										selectedVehicleUi.display
											.serviceImagePath
									}
									width={32}
									height={32}
									alt=""
								/>
							</div>
							<Row className="min-w-0 flex-1 items-center gap-2">
								<Column className="min-w-0 flex-1 gap-0.5">
									<TextLabelLarge2
										className="truncate text-primary"
										text={
											selectedVehicleUi.display
												.serviceShortTitle
										}
									/>
									<TextBodyExtraExtraSmall
										className="line-clamp-2 text-onSurfaceVariant"
										text={
											selectedVehicleUi.display
												.serviceDescription
										}
									/>
								</Column>
								{selectedVehicleUi.showFare ? (
									<TextLabelLarge2
										className="shrink-0 self-center text-primary"
										text={`₱${(selectedVehicleUi.display.price ?? 0).toFixed(2)}`}
									/>
								) : (
									<span className="shrink-0 self-center rounded-full border border-[#CCDFE5] bg-primary99/80 px-1.5 py-px font-poppins text-[7px] font-semibold uppercase tracking-wide text-primary60">
										Coming soon
									</span>
								)}
							</Row>
							<MdArrowDropDown
								size={18}
								className="shrink-0 self-center text-onSurfaceVariant"
							/>
						</Row>
						{vehicleDropdownOpen ? (
							<Column className="absolute left-0 right-0 z-10 max-h-[min(12rem,40vh)] overflow-y-auto rounded-b-[10px] border border-t-0 border-[#CCDFE5] bg-white shadow-sm">
								{vehicleTypesForPricing.map((vehicleType, index) => {
									const vehicle = getDeliverySendVehicleTypeInfo(
										vehicleType.VehicleTypeId,
										vehicleType.Amount,
									);
									const isInactive =
										!readVehicleTypeIsActive(vehicleType);
									const isSelected =
										!isInactive &&
										vehicle.xpressVehicleType ===
											deliveryVehicleTypeId;
									const isLast =
										index ===
										vehicleTypesForPricing.length - 1;
									return (
										<Row
											key={`${vehicleType.FareId}-${vehicleType.VehicleTypeId}`}
											className={cn(
												'items-center gap-2 py-1.5 pl-2.5 pr-2',
												isLast && 'rounded-b-[10px]',
												isInactive
													? 'cursor-not-allowed bg-neutralVariant99/20 opacity-60'
													: 'cursor-pointer',
												!isInactive &&
													!isSelected &&
													'hover:bg-[#DFE3E6]',
												isSelected && 'bg-[#D1D4D6]',
											)}
											onClick={() => {
												if (isInactive) return;
												onDeliveryVehicleTypeIdChange(
													vehicle.xpressVehicleType,
												);
												setVehicleDropdownOpen(false);
											}}
										>
											<img
												src={vehicle.serviceImagePath}
												className={cn(
													'h-7 w-7 shrink-0 self-center object-contain',
													isInactive && 'grayscale',
												)}
												width={28}
												height={28}
												alt=""
											/>
											<Row className="min-w-0 flex-1 items-center gap-2">
												<Column className="min-w-0 flex-1 gap-0">
													<span
														className={cn(
															'line-clamp-1 font-poppins text-label-medium text-onSurface',
															isInactive &&
																'text-onSurfaceVariant',
														)}
													>
														{vehicle.serviceTitle}
													</span>
													<TextBodyExtraExtraSmall
														className="line-clamp-1 text-onSurfaceVariant"
														text={
															vehicle.serviceDescription
														}
													/>
												</Column>
												{isInactive ? (
													<span className="shrink-0 self-center rounded-full border border-[#CCDFE5] bg-primary99/80 px-1.5 py-px font-poppins text-[7px] font-semibold uppercase tracking-wide text-primary60">
														Coming soon
													</span>
												) : (
													<span className="shrink-0 self-center font-poppins text-label-small2 text-onSurfaceVariant tabular-nums">
														₱
														{(vehicle.price ?? 0).toFixed(
															2,
														)}
													</span>
												)}
											</Row>
										</Row>
									);
								})}
							</Column>
						) : null}
					</div>
				) : null}
				{routeGeoReady &&
				!routePricingLoading &&
				!routePricingIsError &&
				vehicleTypesForPricing?.length &&
				!hasActiveVehicleType ? (
					<Row className="min-h-[3rem] items-center rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2">
						<TextBodySmall
							className="text-onSurfaceVariant"
							text="No bookable vehicles are available for this route yet."
						/>
					</Row>
				) : null}
				{routeGeoReady &&
				!routePricingLoading &&
				!routePricingIsError &&
				!vehicleTypesForPricing?.length ? (
					<Row className="min-h-[3rem] items-center rounded-[10px] border border-[#CCDFE5] bg-primary99/40 px-2.5 py-2">
						<TextBodySmall
							className="text-onSurfaceVariant"
							text="No vehicles are available for this route."
						/>
					</Row>
				) : null}
			</Column>

			<Column className="gap-2.5">
				<SectionHeading title="Select Payer" required />
				<Controller
					name="payer"
					control={control}
					render={({ field }) => (
						<Row className="gap-1.5 w-full flex-wrap">
							{PAYER_CARD_DEFS.map((card) => {
								const enabled = isDeliveryPayerSelectable(card.id);
								const sub =
									card.fixedSub ??
									(card.icon === 'sender'
										? senderSubtitle
										: recipientSubtitle);
								const checked = field.value === card.id;
								const select = () => {
									if (!enabled) return;
									field.onChange(card.id);
								};
								return (
									<div
										key={card.id}
										className={cn(
											'relative flex-1 min-w-[6rem] rounded-[12px] border p-2.5 text-left transition',
											checked
												? 'border-primary60 bg-primary99 shadow-sm'
												: 'border-[#CCDFE5] bg-white hover:bg-primary99/50',
											!enabled &&
												'cursor-not-allowed opacity-60 hover:bg-white',
										)}
									>
										{!enabled ? (
											<span className="absolute left-2 top-2 z-[1] rounded-full border border-[#CCDFE5] bg-primary99/90 px-1.5 py-px font-poppins text-[7px] font-semibold uppercase tracking-wide text-primary60">
												Coming soon
											</span>
										) : null}
										<button
											type="button"
											onClick={select}
											disabled={!enabled}
											className={cn(
												'w-full border-0 bg-transparent p-0 text-left',
												enabled
													? 'cursor-pointer'
													: 'cursor-not-allowed',
											)}
										>
											<Column className="w-full gap-1 items-start">
												{card.icon === 'logo' ? (
													<img
														src="/images/concierge/delivery/X%20Icon.svg"
														alt=""
														className="block h-8 w-auto max-w-[4.4rem] object-contain object-left"
													/>
												) : card.icon === 'sender' ? (
													<img
														src="/images/concierge/delivery/CashSender.png"
														alt=""
														className="block h-8 w-auto max-w-[4.4rem] object-contain object-left"
													/>
												) : (
													<img
														src="/images/concierge/delivery/CashReceipt.png"
														alt=""
														className="block h-8 w-auto max-w-[4.4rem] object-contain object-left"
													/>
												)}
												<span className="block w-full font-poppins text-label-large2 text-primary font-semibold">
													{card.title}
												</span>
												<span className="block w-full font-poppins text-body-small text-onSurfaceVariant line-clamp-2">
													{sub}
												</span>
											</Column>
										</button>
										<div className="absolute right-2 top-2">
											<RadioButton
												inputId={`payer-${card.id}`}
												value={card.id}
												checked={checked}
												disabled={!enabled}
												onChange={select}
												pt={{
													box: {
														className:
															programStyle[
																'program-radio-input'
															],
													},
													icon: {
														className:
															programStyle[
																'program-radio-icon'
															],
													},
												}}
											/>
										</div>
									</div>
								);
							})}
						</Row>
					)}
				/>
				{errors.payer ? (
					<TextBodyExtraExtraSmall
						className="text-error60"
						text={errors.payer}
					/>
				) : null}
			</Column>
		</Column>
	);
};

export default NewSendBookingStep2;
